import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { SentimentType } from '@aws-sdk/client-comprehend'
import { Editor } from '@tinymce/tinymce-react'
import { useControl } from 'Contexts/ControlContext'
import { RichTextEditorPropsType } from 'Types/Attribute.types'
import { useTheme } from 'Contexts/ThemeContext'
import Prism from 'prismjs'
import 'prismjs/themes/prism.css'
import { MathTypePlugin } from 'Plugins/MathType/MathType.plugin.ts'
import { handlePasteFunctionality } from 'Utils/common.utils'
import { CodeSampleLanguages, plugins } from 'Utils/richText.utils.ts'
import './RichText.less'
import { useMessage } from 'shared/hooks/use-message'
import { ACTIVITY_EVENTS } from 'Constants/constants'

export type TextAnalysisReportType = {
    sentiment?: SentimentType
    readability?: number
    grammaticalProblemsCount?: number
}

export const RichTextEditor = forwardRef<
    { getContainer: () => HTMLIFrameElement | null },
    RichTextEditorPropsType
>((_props, ref) => {
    const control = useControl()
    const theme = useTheme()
    const [messageApi] = useMessage()
    const {
        onChange: onChangeValue,
        readonly,
        addInputRef,
        updateControlValue,
        keyBindingActions,
        isPasteFunctionalityEnabled,
        value,
        onEditorReady,
        height,
    } = useMemo(() => {
        return { ..._props, ...(control ?? {}) }
    }, [_props, control])

    useEffect(() => {
        // When we navigate to any other page with iMathEQ plugin open, it stays open. So when we change pages, we forcefully close the plugin if it's open
        return () => {
            const iMathEQCloseButton = document.querySelector(
                '.imatheq_modal_close_button'
            ) as HTMLAnchorElement
            if (iMathEQCloseButton) {
                iMathEQCloseButton.click()
            }
        }
    }, [])

    useEffect(() => {
        Prism.highlightAll()
    }, [])

    const updateContent = useCallback(
        (content: string) => {
            if (onChangeValue) {
                onChangeValue(content)
            }
            if (updateControlValue) {
                updateControlValue(content)
            }
        },
        [onChangeValue, updateControlValue]
    )

    const editorRef = useRef<Editor>(null)

    const isMathTypePluginEnabled = (): boolean => {
        return !!_props.installedPlugins.find(item => item instanceof MathTypePlugin)
    }

    /* 
        so that if made non editable to editable in qc, editor can be 
        reinitialized with configs which were disabled during non editable mode 
    */
    const editorKey = useMemo(() => `${_props.id}-${readonly}`, [_props.id, readonly])

    const notEditableInitConfigs = useMemo(
        () =>
            readonly
                ? {
                      toolbar: [],
                      menubar: false,
                      statusbar: false,
                      body_class: 'non-editable-editor',
                  }
                : {},
        [readonly]
    )

    useImperativeHandle(ref, () => ({
        getContainer: () => {
            return editorRef.current?.editor?.iframeElement ?? null
        },
    }))

    return (
        <div data-testid="rich-text-wrapper" className="rich-text-editor-wrapper">
            <Editor
                key={editorKey}
                disabled={readonly}
                value={value}
                onEditorChange={content => {
                    updateContent(content)
                }}
                ref={editorRef}
                init={{
                    statusbar: false,
                    skin: theme === 'light' ? 'oxide' : 'oxide-dark',
                    content_css: theme === 'light' ? 'default' : 'dark',
                    min_height: height ?? 480,
                    max_height: height ?? 480,
                    plugins,
                    toolbar: [
                        'code codesample table | ' +
                            'undo redo | formatselect | ' +
                            'bold italic backcolor | alignleft aligncenter ' +
                            'alignright alignjustify | bullist numlist | ' +
                            'removeformat' +
                            (isMathTypePluginEnabled() ? ' | imatheq' : ''),
                    ],
                    removed_menuitems: isPasteFunctionalityEnabled ? '' : 'paste pastetext',
                    elementpath: false,
                    branding: false,
                    resize: false,
                    init_instance_callback: () => {
                        addInputRef<Editor>?.(editorRef)
                        updateControlValue?.(value ?? '')
                        onEditorReady?.()
                    },
                    external_plugins: {
                        imatheq: `/imatheq/plugin.min.js`,
                    },
                    setup: editor => {
                        editor.on('init', () => {
                            Prism.highlightAll()
                        })
                        editor.on('keydown', keyBindingActions)
                        editor.on('paste', e => {
                            handlePasteFunctionality(
                                e,
                                isPasteFunctionalityEnabled,
                                messageApi.error
                            )
                        })

                        const triggerActivityEvent = (eventName: string) => {
                            // Create and dispatch a synthetic event on the main document
                            // to trigger activity monitoring
                            const syntheticEvent = new Event(eventName, { bubbles: true })
                            document.dispatchEvent(syntheticEvent)
                        }
                        ACTIVITY_EVENTS.forEach(eventName => {
                            editor.on(eventName, () => triggerActivityEvent(eventName))
                        })
                        editor.on('remove', () => {
                            ACTIVITY_EVENTS.forEach(eventName => {
                                editor.off(eventName)
                            })
                        })
                    },
                    htmlAllowedTags: ['.*'],
                    htmlAllowedAttrs: ['.*'],
                    draggable_modal: true,
                    extended_valid_elements:
                        'img[alt|imatheq-mml|imatheq-latex|src|data-mce-selected]',
                    codesample_global_prismjs: true,
                    codesample_languages: CodeSampleLanguages,
                    ...notEditableInitConfigs,
                }}
                id={`${_props.id}`}
                tinymceScriptSrc={'/tinymce/tinymce.min.js'}
            />
        </div>
    )
})
